import React, { createContext, useContext, useEffect, useState } from "react";
import { format } from "date-fns";
import { useTranslate } from "react-polyglot";
import { useHttp } from "../Common/HttpProvider.jsx";
import { useBaseData } from "./BaseDataProvider.jsx";
import { useDialogs } from "../Common/DialogsProvider.jsx";

/**
 * @typedef {Object} ProductCatalogProviderContext
 * @property {} coinOffers
 * @property {FnAsyncVoid} getCoinOffers
 * @property {} transactions
 * @property {FnAsyncVoid} getTransactions
 * @property {FnAsyncVoid} payOffer
 * @property {FnAsyncNumberVoid} startMission
 */

/** @type {React.Context<ProductCatalogProviderContext>} */
const ProductCatalogContext = createContext({
  coinOffers: [],
  /** @type {FnAsyncVoid} */ getCoinOffers: () => {},
  transactions: [],
  /** @type {FnAsyncVoid} */ getTransactions: () => {},
  /** @type {FnAsyncVoid} */ payOffer: () => {},
});

/** @returns {ProductCatalogProviderContext} */
export const useProductCatalog = () => useContext(ProductCatalogContext);

/**
 * @param {React.ReactNode} children
 * @returns {React.ReactNode}
 * @constructor
 */
const ProductCatalogProvider = ({ children }) => {
  const { get, post } = useHttp();
  const { subject, setCoinBalance } = useBaseData();
  const snackbar = useDialogs();
  const t = useTranslate();
  const [coinOffers, setCoinOffers] = useState(/** @type {CoinOffer[]} */ []);
  const [transactions, setTransactions] = useState(/** @type {CoinOffer[]} */ []);

  const subjectId = subject?.subjectId;

  /** */
  const getCoinOffers = async () => {
    const resp = await get("market/offers");
    if (resp) {
      setCoinOffers(resp);
    }
  };

  // load coin offers
  useEffect(() => {
    if (subjectId) {
      getCoinOffers().then();
    } else {
      setCoinOffers([]);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId]);

  /** @type {FnAsyncNumberVoid} */
  const getTransactions = async () => {
    const resp = await get("market/transactions");
    if (resp) {
      const grouped = resp.reduce((acc, tx) => {
        const key = format(new Date(tx.createdAt), "dd.MM.yyyy");

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(tx);

        return acc;
      }, {});
      console.log(grouped);
      const groupedArray = Object.entries(grouped).map(([date, items]) => ({
        date,
        items,
      }));
      console.log(groupedArray);
      setTransactions(groupedArray);
    }
  };

  /** @type {FnPayOfferVoid} */
  const payOffer = async (data) => {
    const resp = await post("market/pay", data, (error) => {
      // Особый случай: сервер отклонил повторную покупку одноразового товара — показываем локализованное сообщение
      snackbar.error(
        error === "one_time_offer_already_purchased" ? t("productCatalog.oneTimeAlreadyPurchased") : error,
      );
    });
    if (resp) {
      setCoinBalance(resp.coinBalance);
    }
  };

  const value = {
    coinOffers,
    getCoinOffers,
    transactions,
    getTransactions,
    payOffer,
  };

  return <ProductCatalogContext.Provider value={value}>{children}</ProductCatalogContext.Provider>;
};

export default ProductCatalogProvider;
